import { useState, useEffect, useMemo } from 'react';
import Select, { SingleValue, components } from 'react-select';
import * as Flags from 'country-flag-icons/react/3x2';

interface CountryOption {
  value: string;  // e.g. "+57"
  label: string;  // e.g. "+57 (Colombia)"
  name: string;
  alpha2: string; // e.g. "CO"
}

// Get flag component by alpha2 code
function FlagIcon({ code, className }: { code: string; className?: string }) {
  const Flag = (Flags as any)[code.toUpperCase()];
  if (!Flag) return <span className={className}>🏳️</span>;
  return <Flag className={className} />;
}

// Custom option
function Option(props: any) {
  return (
    <components.Option {...props}>
      <div className="flex items-center">
        <FlagIcon code={props.data.alpha2} className="w-5 h-4 mr-2 rounded-sm" />
        <span className="text-sm">{props.data.value}</span>
        <span className="text-xs text-gray-500 ml-1.5">({props.data.name})</span>
      </div>
    </components.Option>
  );
}

// Custom selected value
function SingleValueComponent(props: any) {
  return (
    <components.SingleValue {...props}>
      <div className="flex items-center">
        <FlagIcon code={props.data.alpha2} className="w-5 h-4 mr-1.5 rounded-sm" />
        <span className="text-sm font-medium">{props.data.value}</span>
      </div>
    </components.SingleValue>
  );
}

interface PhoneCodeSelectProps {
  value: string;
  onChange: (code: string) => void;
}

const FALLBACK_COUNTRIES: CountryOption[] = [
  { value: '+57', label: '+57 (Colombia)', name: 'Colombia', alpha2: 'CO' },
  { value: '+52', label: '+52 (México)', name: 'México', alpha2: 'MX' },
  { value: '+593', label: '+593 (Ecuador)', name: 'Ecuador', alpha2: 'EC' },
  { value: '+51', label: '+51 (Perú)', name: 'Perú', alpha2: 'PE' },
  { value: '+56', label: '+56 (Chile)', name: 'Chile', alpha2: 'CL' },
  { value: '+54', label: '+54 (Argentina)', name: 'Argentina', alpha2: 'AR' },
  { value: '+58', label: '+58 (Venezuela)', name: 'Venezuela', alpha2: 'VE' },
  { value: '+1', label: '+1 (United States)', name: 'United States', alpha2: 'US' },
  { value: '+34', label: '+34 (España)', name: 'España', alpha2: 'ES' },
  { value: '+55', label: '+55 (Brasil)', name: 'Brasil', alpha2: 'BR' },
  { value: '+507', label: '+507 (Panamá)', name: 'Panamá', alpha2: 'PA' },
  { value: '+506', label: '+506 (Costa Rica)', name: 'Costa Rica', alpha2: 'CR' },
];

export function PhoneCodeSelect({ value, onChange }: PhoneCodeSelectProps) {
  const [countries, setCountries] = useState<CountryOption[]>(FALLBACK_COUNTRIES);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('https://countries.dev/countries?fields=name,alpha2Code,callingCodes')
        .then(res => res.json())
        .catch(() => null),
      fetch('https://ipapi.co/json/')
        .then(res => res.json())
        .catch(() => null),
    ]).then(([countriesData, geoData]) => {
      if (countriesData && Array.isArray(countriesData)) {
        const formatted: CountryOption[] = countriesData
          .filter((c: any) => c.callingCodes?.length > 0 && c.callingCodes[0])
          .map((c: any) => {
            const code = `+${c.callingCodes[0]}`;
            const alpha2 = (c.alpha2Code || '').toUpperCase();
            return {
              value: code,
              label: `${code} (${c.name})`,
              name: c.name,
              alpha2,
            };
          })
          .sort((a: CountryOption, b: CountryOption) => a.name.localeCompare(b.name));

        setCountries(formatted);

        // Auto-detect user's country
        if (geoData?.country_code) {
          const detected = formatted.find(c => c.alpha2 === geoData.country_code.toUpperCase());
          if (detected) {
            onChange(detected.value);
          }
        }
      }
      setIsLoading(false);
    });
  }, []);

  const selectedOption = useMemo(
    () => countries.find(c => c.value === value) || null,
    [countries, value]
  );

  return (
    <Select<CountryOption>
      value={selectedOption}
      onChange={(option: SingleValue<CountryOption>) => {
        if (option) onChange(option.value);
      }}
      options={countries}
      isLoading={isLoading}
      placeholder="País"
      components={{ Option, SingleValue: SingleValueComponent }}
      isSearchable
      menuPortalTarget={document.body}
      menuPosition="fixed"
      filterOption={(option, input) => {
        const s = input.toLowerCase();
        return (
          option.data.name.toLowerCase().includes(s) ||
          option.data.value.includes(s) ||
          option.data.alpha2.toLowerCase().includes(s)
        );
      }}
      styles={{
        control: (base) => ({
          ...base,
          minHeight: '42px',
          borderColor: '#d1d5db',
          borderRadius: '0.5rem',
          '&:hover': { borderColor: '#6366f1' },
          boxShadow: 'none',
          fontSize: '14px',
        }),
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
        menu: (base) => ({
          ...base,
          width: '280px',
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isFocused ? '#eef2ff' : 'white',
          color: '#1f2937',
          cursor: 'pointer',
          padding: '8px 12px',
        }),
        valueContainer: (base) => ({
          ...base,
          padding: '2px 8px',
        }),
        menuList: (base) => ({
          ...base,
          maxHeight: '220px',
        }),
      }}
    />
  );
}
